import React, { useState, useRef, useEffect, useId, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import TextInfo, {
  TEXT_COLOR,
  TEXT_FONT_WEIGHT,
  TEXT_KIND,
  TEXT_SIZE,
} from "@gc-npm/components.text";
import Icon from "@gc-npm/components.icon-glyph";
import { COLOR_DICTIONARY } from "@gc-npm/utils.enums.colors";

const getValueObjFromValueStr = (value, array) => {
  const occurrences = array.filter(
    (element) => String(element.value) === String(value),
  );
  if (occurrences.length === 1) {
    return occurrences[0];
  }
  return null;
};

export const COMPONENT_NAME = "Select";

const getPlaceholderText = (selectedOption, defaultValue) => {
  if (selectedOption?.label) return selectedOption.label;
  if (defaultValue && defaultValue !== "0" && defaultValue !== "undefined") {
    return defaultValue;
  }
  return "Selecciona una opción";
};

const Select = ({
  name,
  label,
  ariaLabel,
  options,
  defaultValue,
  onChange,
  required,
  isSearchable,
  size,
  activeSelectId,
  setActiveSelectId,
  instanceId,
  value,
}) => {
  const [selectedOption, setSelectedOption] = useState(
    () => value || getValueObjFromValueStr(defaultValue, options),
  );
  const [filterString, setFilterString] = useState("");
  const [textInput, setTextInput] = useState(selectedOption?.label || "");
  const [internalOpen, setInternalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const selectRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);
  const typedText = useRef({ value: "", at: 0 });
  const generatedId = useId();
  const selectId = instanceId || generatedId;
  const inputId = `inputInSelect-${generatedId}`;
  const listboxId = `options-${generatedId}`;
  const accessibleLabel = ariaLabel || label || "Selecciona una opción";
  const listIsActive = typeof setActiveSelectId === "function"
    ? activeSelectId === selectId
    : internalOpen;
  const visibleOptions = useMemo(() => options.filter((option) =>
    !isSearchable || String(option.label || "").toLowerCase().includes(filterString)),
  [options, isSearchable, filterString]);
  const enabledIndices = visibleOptions.flatMap((option, index) =>
    option.isDisabled || option.disabled ? [] : [index]);
  const optionId = (index) => `${listboxId}-option-${index}`;
  const setListOpen = useCallback((open) => {
    if (typeof setActiveSelectId === "function") {
      setActiveSelectId((current) => open ? selectId : current === selectId ? null : current);
    } else {
      setInternalOpen(open);
    }
  }, [selectId, setActiveSelectId]);
  const closeList = useCallback(() => {
    setListOpen(false);
    setFilterString("");
    setTextInput(selectedOption?.label || "");
    typedText.current = { value: "", at: 0 };
  }, [selectedOption, setListOpen]);

  useEffect(() => {
    if (!listIsActive) return undefined;
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        closeList();
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [listIsActive, closeList]);

  useEffect(() => {
    if (!listIsActive) return;
    const option = visibleOptions[activeIndex];
    if (!option || option.isDisabled || option.disabled) {
      setActiveIndex(visibleOptions.findIndex((item) => !item.isDisabled && !item.disabled));
      return;
    }
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, listIsActive, visibleOptions]);

  useEffect(() => {
    const nextSelectedOption = value
      || getValueObjFromValueStr(defaultValue, options);
    setSelectedOption(nextSelectedOption);
  }, [value, defaultValue, options]);

  useEffect(() => {
    setTextInput(selectedOption?.label || "");
  }, [selectedOption?.value, selectedOption?.label]);

  const handleChange = (element) => {
    if (!element || element.isDisabled || element.disabled) return;
    const isSameOption = String(selectedOption?.value) === String(element.value);

    if (!isSameOption) {
      setSelectedOption(element);
      setTextInput(element.label);
      if (onChange) onChange(element.value, true, element.label);
    }

    setFilterString("");
    setTextInput(element.label);
    setListOpen(false);
    inputRef.current?.focus();
  };

  const handleFilter = (event) => {
    setTextInput(event.target.value);
    setFilterString(event.target.value.toLowerCase());
    setActiveIndex(-1);
    setListOpen(true);
  };
  const openList = (index) => {
    const selectedIndex = visibleOptions.findIndex((option) =>
      String(option.value) === String(selectedOption?.value)
      && !option.isDisabled && !option.disabled);
    setActiveIndex(index ?? (selectedIndex >= 0 ? selectedIndex : enabledIndices[0] ?? -1));
    setListOpen(true);
  };
  const toggleList = () => {
    inputRef.current?.focus();
    if (listIsActive) closeList();
    else openList();
  };
  const handleSelectKeyDown = (event) => {
    if (event.isComposing || event.nativeEvent?.isComposing) return;
    if (event.key === "Tab") {
      closeList();
      return;
    }
    if (event.key === "Escape") {
      if (listIsActive) {
        event.preventDefault();
        event.stopPropagation();
        closeList();
      }
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      if (!listIsActive) openList();
      else {
        const position = enabledIndices.indexOf(activeIndex);
        const nextPosition = Math.max(0, Math.min(enabledIndices.length - 1, position + direction));
        setActiveIndex(enabledIndices[nextPosition] ?? -1);
      }
      return;
    }
    if (!isSearchable && (event.key === "Home" || event.key === "End")) {
      event.preventDefault();
      event.stopPropagation();
      openList(event.key === "Home" ? enabledIndices[0] : enabledIndices.at(-1));
      return;
    }
    if (event.key === "Enter" || (!isSearchable && event.key === " ")) {
      event.preventDefault();
      event.stopPropagation();
      if (listIsActive) handleChange(visibleOptions[activeIndex]);
      else openList();
      return;
    }
    if (!isSearchable && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      event.stopPropagation();
      const now = Date.now();
      const key = event.key.toLowerCase();
      const previous = now - typedText.current.at < 700 ? typedText.current.value : "";
      const search = previous === key ? key : previous + key;
      typedText.current = { value: search, at: now };
      const start = search.length === 1 && listIsActive ? activeIndex + 1 : 0;
      const ordered = [...enabledIndices.filter((index) => index >= start), ...enabledIndices.filter((index) => index < start)];
      const match = ordered.find((index) => String(visibleOptions[index].label || "").toLowerCase().startsWith(search));
      if (match !== undefined) openList(match);
    }
  };

  return (
    <div
      ref={selectRef}
      className={`Select ${size}`}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) closeList();
      }}
    >
      {label && <label className="label-select" htmlFor={inputId}>{label}</label>}
      <div
        onClick={toggleList}
        className={`custom-select ${label ? "select-with-label" : "noLabel"}`}
      >
        <div className="wrapper-input">
          <label htmlFor={inputId}>
            <input
              ref={inputRef}
              id={inputId}
              role="combobox"
              aria-label={accessibleLabel}
              aria-expanded={listIsActive}
              aria-haspopup="listbox"
              aria-controls={listboxId}
              aria-activedescendant={listIsActive && visibleOptions[activeIndex] ? optionId(activeIndex) : undefined}
              aria-autocomplete={isSearchable ? "list" : "none"}
              required={required && !selectedOption}
              autoComplete="off"
              type="text"
              name={name}
              value={textInput}
              onChange={handleFilter}
              onKeyDown={handleSelectKeyDown}
              className="selected-option"
              readOnly={!isSearchable}
              placeholder={getPlaceholderText(selectedOption, defaultValue)}
            />
            <span className="sr-only">{accessibleLabel}</span>
          </label>
        </div>
        <div className="wrapper-cheuron" aria-hidden="true">
          <Icon
            id={listIsActive ? "cheuron-up-m" : "cheuron-down-m"}
            height={12}
            color={COLOR_DICTIONARY.MOVISTAR_BLUE}
          />
        </div>
      </div>
      <ul id={listboxId} className="options" role="listbox" aria-label={accessibleLabel} hidden={!listIsActive}>
          {visibleOptions.length === 0 && (
            <li role="option" aria-disabled="true" aria-selected="false" className="optionMenu">
              No hay opciones disponibles
            </li>
          )}
          {visibleOptions.map((option, index) => (
              <li
                id={optionId(index)}
                ref={(node) => { optionRefs.current[index] = node; }}
                className={`optionMenu ${String(selectedOption?.value) === String(option.value) ? "option-selected" : ""} ${index === activeIndex ? "option-active" : ""}`}
                onClick={() => handleChange(option)}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => { if (!option.isDisabled && !option.disabled) setActiveIndex(index); }}
                role="option"
                aria-selected={String(selectedOption?.value) === String(option.value)}
                aria-disabled={option.isDisabled || option.disabled || undefined}
                tabIndex={-1}
                key={option.value}
              >
                <span className="optionLabel">
                  <TextInfo
                    text={option.label}
                    size={TEXT_SIZE.CAPTION1}
                    kind={TEXT_KIND.SPAN}
                    color={TEXT_COLOR.GREY6}
                    fontType={TEXT_FONT_WEIGHT.REGULAR}
                  />
                </span>
              </li>
            ))}
      </ul>
      <style jsx>
        {`

          
          .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0,0,0,0);
            white-space: nowrap;
            border: 0;
          }
          .Select {
            margin: 0;
            position: relative;
            margin: 0.5rem 0px 0rem 0;
          }

          .label-select {
            position: absolute;
            z-index: 1;
            left: 12px;
            top: 6px;
            font-family: "Regular";
            color: #605e5c;
          }
          .custom-select {
            position: relative;
            display: inline-block;
            height: 2.1875rem;
            border: solid 1px #dddddd !important;
            width: 100%;
            border-radius: 4px;
            outline: none;
            cursor: pointer;
          }
          .custom-select:focus-within {
            outline: 2px solid #0066ff;
            outline-offset: 2px;
          }
          .custom-select.select-with-label {
            padding: 22px 8px 2px 12px;
          }
          .custom-select.noLabel {
            padding: 7px 8px 7px 12px;
          }
          .wrapper-input {
            position: relative;
            align-items: center;
            display: flex;
          }
          .selected-option {
            cursor: pointer;
            outline: none !important;
            width: calc(100% + 20px);
            color: #605e5c;
          }
          .selected-option::placeholder {
            color: #605e5c;
            opacity: 1;
          }
          .wrapper-cheuron {
            position: absolute;
            top: calc(50% - 6px);
            right: 12px;
          }
          .options {
            top: 2.1875rem;
            position: absolute;
            list-style: none;
            padding: 0;
            margin: 0;
            box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.24);
            border-radius: 4px;
            width: 100%;
            background-color: white;
            z-index: 100;
            overflow: auto;
            max-height: 200px;
          }
          .options[hidden] {
            display: none;
          }
          .optionMenu {
            line-height: 100%;
            font-family: "Regular";
            height: 2.5rem;
            display: flex;
            align-items: center;
          }
          .options li {
            padding: 8px;
            cursor: pointer;
          }
          .options li:hover {
            background-color: #f0f0f0;
            color: #666;
          }
          .options .option-active {
            outline: 2px solid #0066ff;
            outline-offset: -2px;
            background-color: #f0f0f0;
          }

          @media screen and (max-width: 768px) {
            .custom-select {
              height: 2.75rem;
              align-content: center;
            }
            .options {
              z-index: 100;
              top: 2.75rem;
            }
            .Select {
              .wrapper-input {
                min-width: 0;
                flex: 1;
              }
              .selected-option {
                box-sizing: border-box;
                width: 100%;
                min-width: 0;
                padding-right: 1.5rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
              .optionLabel {
                display: block;
                flex: 1;
                width: 100%;
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
              .optionMenu {
                min-width: 0;
              }
            }
          }
        `}
      </style>
    </div>
  );
};

Select.propTypes = {
  /**
   * {
   *  "info": "Nombre del select, se utiliza para identificar el componente.",
   *  "kind": "both",
   *  "beautifulName": "Nombre",
   *  "values": "string"
   * }
   */
  name: PropTypes.string.isRequired,
  /**
   * {
   *  "info": "Etiqueta del select.",
   *  "kind": "both",
   *  "beautifulName": "Etiqueta",
   *  "values": "string"
   * }
   */
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  /**
   * {
   *  "info": "Array de opciones disponibles en el select.",
   *  "kind": "both",
   *  "beautifulName": "Opciones",
   *  "values": "arrayOf({ value: string, label: string })"
   * }
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  /**
   * {
   *  "info": "Valor por defecto del select.",
   *  "kind": "both",
   *  "beautifulName": "Valor por defecto",
   *  "values": "string"
   * }
   */
  defaultValue: PropTypes.string,
  value: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  /**
   * {
   *  "info": "Función que se ejecuta al cambiar el valor del select.",
   *  "kind": "both",
   *  "beautifulName": "onChange",
   *  "values": "function"
   * }
   */
  onChange: PropTypes.func,
  /**
   * {
   *  "info": "Define si el select es obligatorio.",
   *  "kind": "both",
   *  "beautifulName": "Obligatorio",
   *  "values": "boolean"
   * }
   */
  required: PropTypes.bool,
  /**
   * {
   *  "info": "Determina si el select permite buscar entre las opciones.",
   *  "kind": "both",
   *  "beautifulName": "Es Buscable",
   *  "values": "boolean"
   * }
   */
  isSearchable: PropTypes.bool,
  /**
   * {
   *  "info": "Información adicional para etiquetas de seguimiento (tagging).",
   *  "kind": "both",
   *  "beautifulName": "Tagging Info",
   *  "values": "object"
   * }
   */
  taggingInfo: PropTypes.object,
  /**
   * {
   *  "info": "Define el tamaño del select, puede ser 'small', 'medium' o 'large'.",
   *  "kind": "both",
   *  "beautifulName": "Tamaño",
   *  "values": "'small', 'medium', 'large'"
   * }
   */
  size: PropTypes.oneOf(["small", "medium", "large"]),
};

Select.defaultProps = {
  defaultValue: "",
  label: "",
  ariaLabel: "",
  onChange: () => {},
  required: false,
  isSearchable: false,
  taggingInfo: {},
  size: "medium",
};

export default Select;
